import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { eachDayOfInterval } from "date-fns";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import moment from "moment";
import { Tooltip } from "react-tooltip";
import { MessageCircle, ShieldCheck } from "lucide-react";

import ButtonPrimary from "shared/Button/ButtonPrimary";
import CustomDatePickerV2 from "shared/DateField/CustomDatePickerV2";
import NomadDatePicker from "shared/DateField/NomadDatePicker";
import FormItem from "containers/PageAddListing1/FormItem";
import SelectGuests from "containers/ListingDetailPage/listing-stay-detail/SelectGuests";
import CommonModal from "shared/Modal/CommonModal";
import ContactOwnerModal from "containers/ListingDetailPage/listing-stay-detail/contact-owner";

import { currencySymbol } from "shared/CurrencySymbol";
import successHandler from "utils/helpers/SuccessHandler";
import ErrorHandler from "utils/helpers/ErrorHandler";
import { isAuthenticated } from "utils/helpers/CommonHelper";
import { reserveBooking } from "redux/reducers/Payments/PaymentReducer";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";

interface BookingSidebarProps {
  propertyData: any;
  propertySlug: string | undefined;
  currentUser: any;
  formattedDate: string;
  handleChatButtonClick: () => void;
  setInfoModal: React.Dispatch<React.SetStateAction<boolean>>;
  infoModal: boolean;
  setModalDescription: React.Dispatch<React.SetStateAction<string>>;
  modalDescription: string;
  isModalOpen: boolean;
  handleCloseModal: () => void;
  handleSendMessage: (data?: any) => void;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  rooms?: any[];
  selectedRoom?: PropertyToEdit["rooms"][number] | null;
}

const BookingSidebar: React.FC<BookingSidebarProps> = ({
  propertyData,
  propertySlug,
  currentUser,
  formattedDate,
  handleChatButtonClick,
  setInfoModal,
  infoModal,
  setModalDescription,
  modalDescription,
  isModalOpen,
  handleCloseModal,
  handleSendMessage,
  isLoading,
  setIsLoading,
  rooms,
  selectedRoom,
}) => {
  dayjs.extend(isBetween);
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();

  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [error, setError] = useState("");

  const isGuestAllowed = propertyData?.allow_extra_guest;
  const isSharedProperty = propertyData?.is_shared_property;

  // ✅ Use room's availability for shared properties, property's for entire
  const nextAvailableDate =
    isSharedProperty && selectedRoom
      ? selectedRoom.next_available_date
      : propertyData?.next_available_date;

  // ✅ Use room's booked dates for shared properties, property's for entire
  const bookedDates =
    isSharedProperty && selectedRoom
      ? selectedRoom?.booked_dates || []
      : propertyData?.booked_dates || [];

  const isContactEnabled =
    !isSharedProperty || (isSharedProperty && selectedRoom);

  

  // ✅ Calculate minimum date based on availability
  const getMinimumDate = () => {
    // For shared properties without room selection, disable all dates
    if (isSharedProperty && !selectedRoom) {
      return new Date(2099, 0, 1); // Far future date to disable everything
    }

    // If next_available_date exists and is in the future, use it
    if (nextAvailableDate && typeof nextAvailableDate === "string") {
      const availDate = moment(nextAvailableDate);
      if (availDate.isAfter(moment())) {
        return availDate.toDate();
      }
    }

    // Otherwise use availability_start or today
    const availStart =
      isSharedProperty && selectedRoom
        ? selectedRoom.availability_start || propertyData?.availability_start
        : propertyData?.availability_start;

    if (availStart && moment(availStart).isAfter(moment())) {
      return moment(availStart).toDate();
    }

    return new Date();
  };

  // ✅ Calculate maximum date based on availability_end for rooms
  const getMaximumDate = () => {
    // For shared properties with selected room, use room's availability_end
    if (isSharedProperty && selectedRoom?.availability_end) {
      return moment(selectedRoom.availability_end).toDate();
    }

    // For entire properties or fallback, use property's availability_end
    if (propertyData?.availability_end) {
      return moment(propertyData.availability_end).toDate();
    }

    // No maximum date
    return undefined;
  };

  // ✅ Price logic: Use selectedRoom price if shared, otherwise property price
  const pricePerMonth = parseFloat(
    isSharedProperty
      ? selectedRoom?.price_per_month || 0
      : propertyData?.price_per_month || 0
  );

  const maximumGuests = isSharedProperty
    ? Number(selectedRoom?.max_guests || propertyData?.guest_number)
    : Number(propertyData?.guest_number);

  const serviceFeePercent = propertyData?.service_fee;
  const extraGuestFee = propertyData?.per_extra_guest_amount;

  const formik = useFormik({
    initialValues: {
      number_of_guests: null,
      check_in_date: null,
      check_out_date: null,
    },
    enableReinitialize: true,
    onSubmit: (values) => handleSubmit(values),
  });

  const { check_in_date, check_out_date, number_of_guests } = formik.values;

 

  const extraGuestCount =
    isGuestAllowed && number_of_guests && number_of_guests > maximumGuests
      ? number_of_guests - maximumGuests
      : 0;

  const extraAmount = extraGuestCount * parseFloat(extraGuestFee || 0);
  const subtotal = pricePerMonth + extraAmount;
  const serviceFee = (subtotal * parseFloat(serviceFeePercent || 0)) / 100;
  const totalAmount = subtotal + serviceFee;

  const getDisabledDates = (dateRanges: [string, string][]) => {
    const disabled: Date[] = [];
    dateRanges.forEach(([start, end]) => {
      if (start && end) {
        disabled.push(
          ...eachDayOfInterval({
            start: moment(start).toDate(),
            end: moment(end).toDate(),
          })
        );
      }
    });
    return disabled;
  };

  // ✅ Check if room has ANY valid availability windows
  const hasValidAvailability = (room: PropertyToEdit["rooms"][number]) => {
    if (!room) return false;

    const bookings = (room.booked_dates || []) as [string, string][];

    const actualStart = room.next_available_date
      ? moment(room.next_available_date)
      : moment(room.availability_start || propertyData?.availability_start);

    const roomEnd = moment(room.availability_end);

    if (!roomEnd.isValid()) return false;

    // No bookings - check total period
    if (bookings.length === 0) {
      return roomEnd.diff(actualStart, "days") >= 30;
    }

    const sortedBookings = [...bookings].sort((a, b) =>
      moment(a[0]).diff(moment(b[0]))
    );

    // Check gap before first booking
    const firstGap = moment(sortedBookings[0][0]).diff(actualStart, "days");
    if (firstGap >= 30) return true;

    // Check gaps between bookings
    for (let i = 0; i < sortedBookings.length - 1; i++) {
      const gap =
        moment(sortedBookings[i + 1][0]).diff(
          moment(sortedBookings[i][1]),
          "days"
        ) - 1;
      if (gap >= 30) return true;
    }

    // Check gap after last booking
    const lastGap =
      roomEnd.diff(
        moment(sortedBookings[sortedBookings.length - 1][1]),
        "days"
      ) - 1;
    if (lastGap >= 30) return true;

    return false;
  };

  // ✅ Check if selected room has valid availability
  const selectedRoomHasAvailability = selectedRoom
    ? hasValidAvailability(selectedRoom)
    : false;

  // ✅ IMPROVED: Calculate which dates should be disabled based on 30-day minimum booking requirement
  const getInvalidDateRanges = () => {
    if (!isSharedProperty || !selectedRoom) {
      return [];
    }

    const invalidDates: Date[] = [];
    const bookings = (selectedRoom.booked_dates || []) as [string, string][];

    // ✅ Use next_available_date as the actual start if it exists, otherwise use availability_start
    const actualStartDate = selectedRoom.next_available_date
      ? moment(selectedRoom.next_available_date)
      : moment(
          selectedRoom.availability_start ||
            propertyData?.availability_start ||
            new Date()
        );

    const roomEnd = moment(
      selectedRoom.availability_end || propertyData?.availability_end
    );

    // If no room end or room end is invalid, we can't calculate properly
    if (!roomEnd.isValid()) {
      console.warn("Room has no valid availability_end date");
      return invalidDates;
    }

    // Sort bookings by start date
    const sortedBookings = [...bookings].sort((a, b) =>
      moment(a[0]).diff(moment(b[0]))
    );

    // CASE 1: No bookings - check if entire period is < 30 days
    if (sortedBookings.length === 0) {
      const totalDays = roomEnd.diff(actualStartDate, "days");
      if (totalDays < 30) {
        // Entire period is too short - disable all dates
        const current = actualStartDate.clone();
        while (current.isSameOrBefore(roomEnd)) {
          invalidDates.push(current.toDate());
          current.add(1, "day");
        }
      }
      return invalidDates;
    }

    // CASE 2: Check gap from actual start date to first booking
    const firstBookingStart = moment(sortedBookings[0][0]);
    const daysBeforeFirstBooking = firstBookingStart.diff(
      actualStartDate,
      "days"
    );

    if (daysBeforeFirstBooking < 30 && daysBeforeFirstBooking > 0) {
      // Gap too small - disable all dates from actual start to first booking start
      const current = actualStartDate.clone();
      while (current.isBefore(firstBookingStart)) {
        invalidDates.push(current.toDate());
        current.add(1, "day");
      }
    }

    // CASE 3: Check gaps between consecutive bookings
    for (let i = 0; i < sortedBookings.length - 1; i++) {
      const currentBookingEnd = moment(sortedBookings[i][1]);
      const nextBookingStart = moment(sortedBookings[i + 1][0]);

      // Calculate the gap (excluding the booking end date itself)
      const gapDays = nextBookingStart.diff(currentBookingEnd, "days") - 1;

      if (gapDays < 30 && gapDays > 0) {
        // Gap too small - disable all dates between these bookings
        const current = currentBookingEnd.clone().add(1, "day");
        while (current.isBefore(nextBookingStart)) {
          invalidDates.push(current.toDate());
          current.add(1, "day");
        }
      }
    }

    // CASE 4: Check gap from last booking to room end
    const lastBookingEnd = moment(sortedBookings[sortedBookings.length - 1][1]);
    const daysAfterLastBooking = roomEnd.diff(lastBookingEnd, "days") - 1;

    if (daysAfterLastBooking < 30 && daysAfterLastBooking > 0) {
      // Gap too small - disable all dates from last booking end to room end
      const current = lastBookingEnd.clone().add(1, "day");
      while (current.isSameOrBefore(roomEnd)) {
        invalidDates.push(current.toDate());
        current.add(1, "day");
      }
    }

    return invalidDates;
  };

  const disabledDates = getDisabledDates(bookedDates);
  const invalidRangeDates = getInvalidDateRanges();
  const allDisabledDates = [...disabledDates, ...invalidRangeDates];

  // ✅ DEBUG: Log availability analysis (placed after variable declarations)
  React.useEffect(() => {
    console.group("🏠 BookingSidebar Availability Debug");
    console.log("🔑 is_shared_property:", isSharedProperty);
    console.log("🚪 Selected room:", selectedRoom?.name);
    console.log(
      "📅 Room availability_start:",
      selectedRoom?.availability_start
    );
    console.log("🔚 Room availability_end:", selectedRoom?.availability_end);
    console.log(
      "📅 Room next_available_date:",
      selectedRoom?.next_available_date
    );
    console.log("🚫 Room booked_dates:", selectedRoom?.booked_dates);

    // Detailed availability window analysis
    if (isSharedProperty && selectedRoom) {
      const bookings = (selectedRoom.booked_dates || []) as [string, string][];

      // ✅ Use next_available_date as actual start if it exists
      const actualStart = selectedRoom.next_available_date
        ? moment(selectedRoom.next_available_date)
        : moment(
            selectedRoom.availability_start || propertyData?.availability_start
          );

      const roomEnd = moment(selectedRoom.availability_end);

      console.log("\n📊 AVAILABILITY WINDOW ANALYSIS:");
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
      console.log(
        `Actual Start: ${actualStart.format("MMM DD, YYYY")} ${
          selectedRoom.next_available_date
            ? "(next_available_date)"
            : "(availability_start)"
        }`
      );
      console.log(
        `Room End: ${
          roomEnd.isValid() ? roomEnd.format("MMM DD, YYYY") : "No end"
        }`
      );
      console.log(
        `Total Period: ${
          roomEnd.isValid() ? roomEnd.diff(actualStart, "days") : "?"
        } days`
      );
      console.log(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`);

      if (bookings.length === 0) {
        const totalDays = roomEnd.isValid()
          ? roomEnd.diff(actualStart, "days")
          : null;
        console.log("📅 No existing bookings");
        if (totalDays !== null) {
          console.log(
            `   ${
              totalDays >= 30 ? "✅ VALID" : "❌ INVALID"
            }: ${totalDays} days ${
              totalDays >= 30 ? "(≥30 days required)" : "(Need 30+ days)"
            }`
          );
        }
      } else {
        const sortedBookings = [...bookings].sort((a, b) =>
          moment(a[0]).diff(moment(b[0]))
        );

        console.log(`📅 Found ${sortedBookings.length} booking(s):\n`);
        sortedBookings.forEach((booking, idx) => {
          const duration = moment(booking[1]).diff(moment(booking[0]), "days");
          console.log(
            `   ${idx + 1}. ${moment(booking[0]).format("MMM DD")} → ${moment(
              booking[1]
            ).format("MMM DD, YYYY")} (${duration} days)`
          );
        });

        console.log(`\n🔍 Checking gaps (need 30+ days):\n`);

        // Gap 1: Before first booking (from actual start)
        const firstGap = moment(sortedBookings[0][0]).diff(actualStart, "days");
        console.log(
          `   ${
            firstGap >= 30 ? "✅" : "❌"
          } Before booking 1: ${firstGap} days`
        );
        if (firstGap < 30 && firstGap > 0) {
          console.log(
            `      → Disabling: ${actualStart.format("MMM DD")} to ${moment(
              sortedBookings[0][0]
            )
              .subtract(1, "day")
              .format("MMM DD")}`
          );
        }

        // Gaps between bookings
        for (let i = 0; i < sortedBookings.length - 1; i++) {
          const gap =
            moment(sortedBookings[i + 1][0]).diff(
              moment(sortedBookings[i][1]),
              "days"
            ) - 1;
          console.log(
            `   ${gap >= 30 ? "✅" : "❌"} Between booking ${i + 1} and ${
              i + 2
            }: ${gap} days`
          );
          if (gap < 30 && gap > 0) {
            console.log(
              `      → Disabling: ${moment(sortedBookings[i][1])
                .add(1, "day")
                .format("MMM DD")} to ${moment(sortedBookings[i + 1][0])
                .subtract(1, "day")
                .format("MMM DD")}`
            );
          }
        }

        // Gap N: After last booking
        if (roomEnd.isValid()) {
          const lastGap =
            roomEnd.diff(
              moment(sortedBookings[sortedBookings.length - 1][1]),
              "days"
            ) - 1;
          console.log(
            `   ${
              lastGap >= 30 ? "✅" : "❌"
            } After last booking: ${lastGap} days`
          );
          if (lastGap < 30 && lastGap > 0) {
            console.log(
              `      → Disabling: ${moment(
                sortedBookings[sortedBookings.length - 1][1]
              )
                .add(1, "day")
                .format("MMM DD")} to ${roomEnd.format("MMM DD")}`
            );
          }
        }
      }

      console.log(`\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
    }

    console.log("\n📊 Disabled Dates Summary:");
    console.log("   🚫 Booked dates:", disabledDates.length);
    console.log("   ❌ Invalid gaps (<30 days):", invalidRangeDates.length);
    console.log("   🔒 Total disabled:", allDisabledDates.length);
    console.groupEnd();
  }, [
    isSharedProperty,
    selectedRoom,
    propertyData,
    disabledDates,
    invalidRangeDates,
    allDisabledDates,
  ]);

  const handleSubmit = async (values: any) => {
    if (!isAuthenticated()) {
      navigate("/login", { state: { from: location.pathname } });
      return;
    }

    if (!currentUser?.profile_complete) {
      setInfoModal(true);
      setModalDescription(
        "Please complete your profile before making a booking."
      );
      return;
    }

    if (!propertySlug) {
      ErrorHandler(
        "Property information is not available. Please refresh the page."
      );
      setIsLoading(false);
      return;
    }

    // ✅ Only require room selection for shared properties
    if (isSharedProperty && !selectedRoom?.id) {
      setError("Please select a room before booking");
      ErrorHandler("Please select a room before booking");
      return;
    }

    setIsLoading(true);
    try {
      const calcMonthsDiff = (start: string, end: string) => {
        const s = moment(start, "YYYY-MM-DD");
        const e = moment(end, "YYYY-MM-DD");
        const months = e.diff(s, "months", true);
        return Math.max(1, Math.ceil(months));
      };

      const checkInDate = moment(values.check_in_date).format("YYYY-MM-DD");
      const checkOutDate = moment(values.check_out_date).format("YYYY-MM-DD");

      if (!moment(checkInDate).isValid() || !moment(checkOutDate).isValid()) {
        throw new Error("Invalid date format");
      }

      // ✅ Build booking data based on property type
      const bookingData: any = {
        number_of_guests: Number(values.number_of_guests),
        service_fee_amount: parseFloat(serviceFee.toFixed(2)),
        property_id: propertySlug,
        extra_guest_amount: parseFloat(extraAmount.toFixed(2)),
        check_in_date: checkInDate,
        check_out_date: checkOutDate,
        no_of_months: calcMonthsDiff(checkInDate, checkOutDate),
      };

      /*  // ✅ Only add line_items_attributes for shared properties
      if (isSharedProperty && selectedRoom) {
        bookingData.line_items_attributes = [
          {
            room_id: selectedRoom.id,
            price: selectedRoom.price_per_month,
            quantity: 1,
            property_id: propertySlug,
          },
        ];
      } */

      // Shared Property → send 1 selected room
      if (isSharedProperty && selectedRoom) {
        bookingData.line_items_attributes = [
          {
            room_id: selectedRoom.id,
            price: selectedRoom.price_per_month,
            quantity: 1,
            property_id: propertySlug,
          },
        ];
      }

      // Entire Property → send ALL rooms
      if (!isSharedProperty && rooms && rooms.length > 0) {
        bookingData.line_items_attributes = rooms.map((room: any) => ({
          room_id: room.id,
          price: room.price_per_month,
          quantity: 1,
          property_id: propertySlug,
        }));
      }

      const postData = { booking: bookingData };

      console.log("Booking Sidebar payload:", postData);

      const action = await dispatch(reserveBooking(postData as any));

      if (action?.type === "payment/reserveBooking/fulfilled") {
        successHandler("Booking request sent! Await owner confirmation.");
        formik.resetForm();
      } else if (action?.type === "payment/reserveBooking/rejected") {
        console.error("Booking rejected:", action.payload);
        throw new Error(
          typeof action.payload === "string" ? action.payload : "Booking failed"
        );
      }
    } catch (error) {
      console.error("Booking error:", error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to reserve booking. Please try again.";

      ErrorHandler(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsButtonDisabled(!(check_in_date && check_out_date && number_of_guests));
  }, [formik.values]);

  // ✅ Clear dates when selected room changes (for shared properties)
  useEffect(() => {
    if (isSharedProperty && selectedRoom) {
      // Clear the dates when a new room is selected
      formik.setFieldValue("check_in_date", null);
      formik.setFieldValue("check_out_date", null);
    }
  }, [selectedRoom?.id]); // Only trigger when room ID changes

  if (!propertyData) return null;

  const disableDate = (date: Date) =>
    allDisabledDates.some(
      (d) =>
        date.getFullYear() === d.getFullYear() &&
        date.getMonth() === d.getMonth() &&
        date.getDate() === d.getDate()
    );

  const showPricing = check_in_date && check_out_date && pricePerMonth > 0;

   const selectedMonths =
  check_in_date && check_out_date
    ? Math.ceil(
        moment(check_out_date).diff(moment(check_in_date), "months", true)
      )
    : null;

const minimumStay = propertyData?.minimum_months_of_booking || 1;
const isBelowMinimum = selectedMonths !== null && selectedMonths < minimumStay;


console.log("🗓️ Stay Duration Debug:", {
  check_in_date,
  check_out_date,
  selectedMonths,
  minimumStay,
  isBelowMinimum,
  raw_minimum_stay: propertyData?.minimum_months_of_booking,
  raw_diff: check_in_date && check_out_date
    ? moment(check_out_date).diff(moment(check_in_date), "months", true)
    : "no dates",
});

  return (
    <div className="shadow-xl rounded-2xl lg:rounded-3xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden sticky top-4 lg:top-24">
      {/* Compact Price Header */}
      <div className="p-4 lg:p-5 pb-3 lg:pb-4 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-baseline gap-1">
          <span className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">
            {currencySymbol()}
            {pricePerMonth > 0 ? pricePerMonth.toFixed(0) : "--"}
          </span>
          <span className="text-xs lg:text-sm text-neutral-500 font-normal">
            /month
          </span>
        </div>

        {/* Selected Room Badge */}
        {isSharedProperty && selectedRoom && (
          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-full border border-blue-200 dark:border-blue-800">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
            <span className="text-xs font-medium text-blue-900 dark:text-blue-100">
              {selectedRoom.name}
            </span>
          </div>
        )}

        {/* Room Selection Prompt for Shared Properties */}
        {isSharedProperty && !selectedRoom && (
          <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
            Select a room to see pricing
          </div>
        )}

        {/* ✅ Warning if selected room has no valid availability */}
        {isSharedProperty && selectedRoom && !selectedRoomHasAvailability && (
          <div className="mt-2 px-2 py-1.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-xs text-red-700 dark:text-red-300 font-medium">
              ⚠️ No 30+ day availability windows
            </p>
          </div>
        )}
      </div>

      {/* Form Section */}
      <form onSubmit={formik.handleSubmit} className="p-4 lg:p-5 space-y-3">
        {/* Date Pickers - Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              Check-in
            </label>
            <NomadDatePicker
              placeholder="Add date"
              disabled={
                (isSharedProperty && !selectedRoom) || // No room selected
                (isSharedProperty &&
                  selectedRoom &&
                  !selectedRoomHasAvailability) || // ✅ Room has no valid availability
                (typeof propertyData?.next_available_date === "string" &&
                  propertyData.next_available_date !== formattedDate)
              }
              value={check_in_date}
              onChange={(val) => {
                formik.setFieldValue("check_in_date", val);
                // Clear check-out when check-in changes
                formik.setFieldValue("check_out_date", null);
              }}
              minDate={getMinimumDate()} // ✅ Use dynamic minimum date
              maxDate={getMaximumDate()} // ✅ Use room's availability_end or property's availability_end
              excludeDate={disableDate}
            />
            {/* ✅ Show helper text for shared properties */}
            {isSharedProperty && !selectedRoom && (
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                Select a room first to choose dates
              </p>
            )}
            {isSharedProperty &&
              selectedRoom &&
              !selectedRoomHasAvailability && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  This room has no valid 30+ day booking windows
                </p>
              )}
            {isSharedProperty &&
              selectedRoom &&
              selectedRoomHasAvailability &&
              nextAvailableDate && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {/* Only show next available date, don't show misleading range */}
                  Next available:{" "}
                  {moment.utc(nextAvailableDate).local().format("MMM DD, YYYY")}
                </p>
              )}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
              Check-out
            </label>
            <NomadDatePicker
              placeholder="Add date"
              disabled={
                (isSharedProperty && !selectedRoom) || // No room selected
                (isSharedProperty &&
                  selectedRoom &&
                  !selectedRoomHasAvailability) || // ✅ Room has no valid availability
                !check_in_date || // ✅ Disable if no check-in selected
                (typeof propertyData?.next_available_date === "string" &&
                  propertyData.next_available_date !== formattedDate)
              }
              value={check_out_date}
              onChange={(val) => formik.setFieldValue("check_out_date", val)}
              minDate={
                check_in_date
                  ? moment(check_in_date).add(30, "days").toDate() // ✅ At least 30 days (1 month)
                  : getMinimumDate()
              }
              maxDate={getMaximumDate()} // ✅ Use room's availability_end or property's availability_end
              excludeDate={disableDate}
            />
          </div>
        </div>
    {/* Stay Duration Warning */}
{selectedMonths !== null && (
  <div
    className={`px-3 py-2.5 rounded-lg border text-xs flex items-center gap-2 ${
      isBelowMinimum
        ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-300"
        : "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
    }`}
  >
    <span className="flex-shrink-0">{isBelowMinimum ? "⚠️" : "✅"}</span>
    <p>
      {isBelowMinimum ? (
        <>Below minimum ({minimumStay}mo) — but you're welcome to send a request.</>
      ) : (
        <><strong>{selectedMonths} month{selectedMonths !== 1 ? "s" : ""}</strong> — meets the minimum stay.</>
      )}
    </p>
  </div>
)}
        {/* Guests - Full Width on Mobile */}
        <div>
          <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">
            Guests
          </label>
          <SelectGuests
            className="flex-1"
            room={selectedRoom || rooms?.[0] || {}}
            maxGuests={isGuestAllowed ? 15 : maximumGuests}
            formik={formik}
            disabled={
              (isSharedProperty && !selectedRoom) || // ✅ Disable for shared property without room selection
              (typeof propertyData?.next_available_date === "string" &&
                propertyData.next_available_date !== formattedDate)
            }
          />
        </div>
        {/* Price Summary - Only show if we have pricing */}
        {showPricing && (
          <div className="pt-3 mt-3 border-t border-neutral-200 dark:border-neutral-700 space-y-2">
            <PriceRow label="Monthly rent" value={pricePerMonth} />
            {extraGuestCount > 0 && (
              <PriceRow
                label={`Extra guest (${extraGuestCount})`}
                value={extraAmount}
              />
            )}
            <PriceRow
              label="Service fee"
              value={serviceFee}
              tooltip="Platform & support services"
            />
            <div className="pt-2 mt-2 border-t border-neutral-200 dark:border-neutral-700">
              <PriceRow label="Total" value={totalAmount} bold />
            </div>
          </div>
        )}
        {/* Action Buttons - Responsive */}
        <div className="space-y-2 pt-3">
          <ButtonPrimary
            disabled={
              isButtonDisabled ||
              !!error ||
              (isSharedProperty && !selectedRoom) ||
              (isSharedProperty && selectedRoom && !selectedRoomHasAvailability) // ✅ Disable if no valid availability
            }
            onClick={() => handleSubmit(formik.values)}
            loading={isLoading}
            className="w-full !py-2.5 lg:!py-3 text-sm lg:text-base"
          >
            {isSharedProperty && !selectedRoom
              ? "Select a room first"
              : isSharedProperty && selectedRoom && !selectedRoomHasAvailability
              ? "No availability"
              : "Reserve"}
          </ButtonPrimary>

          <button
            type="button"
            onClick={handleChatButtonClick}
            disabled={!isContactEnabled}
            className={`w-full flex items-center justify-center gap-2 py-2.5 lg:py-3 px-4 rounded-xl 
    border border-neutral-300 dark:border-neutral-700 
    bg-white dark:bg-neutral-800 
    text-gray-700 dark:text-gray-200 text-sm font-medium 
    hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors
    ${!isContactEnabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <MessageCircle size={16} className="flex-shrink-0" />
            <span className="truncate">Contact Host</span>
          </button>

          {/* ✅ Payment Notice - Only show when pricing is displayed */}
          {showPricing && (
            <div className="flex items-center gap-2 pt-2 px-3 py-2.5 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800">
              <ShieldCheck
                size={16}
                className="text-green-600 dark:text-green-400 flex-shrink-0"
              />
              <p className="text-xs text-green-800 dark:text-green-300 font-medium">
                You won't pay anything yet.
              </p>
            </div>
          )}
        </div>
      </form>

      {/* Modals */}
      {infoModal && (
        <CommonModal
          isOpen={infoModal}
          onClose={() => setInfoModal(false)}
          description={modalDescription}
        />
      )}

      {isModalOpen && (
        <ContactOwnerModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSend={handleSendMessage}
          propertyData={propertyData}
          currentRoomId={selectedRoom?.id}
          selectedRoom={selectedRoom} // ✅ Add this line
          roomHasAvailability={selectedRoomHasAvailability}
        />
      )}
    </div>
  );
};

export default BookingSidebar;

/* Compact Price Row Component - Mobile Responsive */
const PriceRow = ({
  label,
  value,
  tooltip,
  bold,
}: {
  label: string;
  value: number | string;
  tooltip?: string;
  bold?: boolean;
}) => (
  <div className="flex justify-between items-center">
    <div className="flex items-center gap-1">
      <span
        className={`text-xs lg:text-sm ${
          bold
            ? "font-semibold text-gray-900 dark:text-white"
            : "text-gray-600 dark:text-gray-400"
        }`}
      >
        {label}
      </span>
      {tooltip && (
        <>
          <span
            data-tooltip-id={`tip-${label}`}
            className="text-gray-400 hover:text-gray-600 cursor-help"
          >
            ⓘ
          </span>
          <Tooltip
            id={`tip-${label}`}
            place="top"
            content={tooltip}
            style={{
              backgroundColor: "#0EA5E9",
              color: "white",
              borderRadius: "0.5rem",
              padding: "0.375rem 0.75rem",
              fontSize: "0.75rem",
            }}
          />
        </>
      )}
    </div>
    <span
      className={`text-xs lg:text-sm ${
        bold
          ? "font-semibold text-gray-900 dark:text-white"
          : "text-gray-700 dark:text-gray-300"
      }`}
    >
      {`${currencySymbol()}${Number(value || 0).toFixed(2)}`}
    </span>
  </div>
);
